<script setup>

import { inject, computed } from 'vue';
import TabView from 'primevue/tabview';
import TabPanel from 'primevue/tabpanel';
import PanelRedimensionnable from '@/components/generiques/PanelRedimensionnable.vue';
import OngletTypesObjet from './ongletTypesObjet/OngletTypesObjet.vue'
import OngletDimensions from './ongletDimensions/OngletDimensions.vue'
import OngletParametres from './ongletParametres/OngletParametres.vue'
import OngletConstantes from './ongletConstantes/OngletConstantes.vue'
import OngletListeValeurs from './ongletListeValeurs/OngletListeValeurs.vue'
import OngletImports from './ongletImports/OngletImports.vue'
import OngletRoutines from './ongletRoutines/OngletRoutines.vue'

const { schema } = defineProps({
  schema: Object
})

const ongletsSelectionnes = inject('numOngletsSchemaSelectionnes');

const presenceTypeObjetEnErreur = computed(() => {
	if (!schema) {
		return null;
	}
	return schema.typesObjet.flatMap(typeObj => typeObj.champs).some(champ => champ.formuleEnErreur);
});

const presenceDimensionEnErreur = computed(() => {
	if (!schema) {
		return null;
	}
	return schema.dimensions.some(dimension => dimension.formuleEnErreur);
});

const presenceImportEnErreur = computed(() => {
	if (!schema) {
		return null;
	}
	return schema.imports.some(imp => imp.formuleEnErreur);
});

const presenceRoutineEnErreur = computed(() => {
	if (!schema) {
		return null;
	}
	return schema.routines.some(routine => routine.formuleEnErreur);
});

</script>

<template>
	<PanelRedimensionnable class="panel-gauche panel-lateral" :position="'gauche'" :largeurIni="200">
		<TabView 
		  v-model:activeIndex="ongletsSelectionnes.numOngletPrincipal">
			<TabPanel>
				<template #header>
					<div 
					  :class="{erreur: presenceTypeObjetEnErreur}" 
					v-tooltip="'Types d\'objet et champs'">
						<i class="fas fa-sitemap icone-rubrique"></i>
					</div>
				</template>
				<OngletTypesObjet :types-objet="schema.typesObjet" />
			</TabPanel>
			<TabPanel>
				<template #header>
					<div :class="{erreur: presenceDimensionEnErreur}" 
					v-tooltip="'Dimensions et paramètres'">
						<i class="fas fa-cube icone-rubrique"></i>
					</div>
				</template>
				<TabView 
					v-model:activeIndex="ongletsSelectionnes.numSousOngletDimEtParam">
					<TabPanel>
						<template #header>
							<div :class="{erreur: presenceDimensionEnErreur}" 
							v-tooltip="'Dimensions'">
								<i class="fas fa-ruler-horizontal icone-rubrique"></i>
							</div>
						</template>
						<OngletDimensions :dimensions="schema.dimensions" />
					</TabPanel>
					<TabPanel>
						<template #header>
							<div v-tooltip="'Paramètres'"><i class="fas fa-sliders icone-rubrique"></i></div>
						</template>
						<OngletParametres :parametres="schema.parametres" />
					</TabPanel>
				</TabView>
			</TabPanel>
			<TabPanel>
				<template #header>
					<div :class="{erreur: presenceImportEnErreur}" 
					v-tooltip="'Imports'">
						<i class="fas fa-upload icone-rubrique"></i>
					</div>
				</template>
				<TabView v-model:activeIndex="ongletsSelectionnes.numSousOngletImport">
					<TabPanel>
						<template #header>
							<div :class="{erreur: presenceImportEnErreur}" 
							v-tooltip="'Imports de fichier'">
								<i class="fas fa-file icone-rubrique"></i>
							</div>
						</template>
						<OngletImports :imports="schema.imports" />
					</TabPanel>
					<TabPanel>
						<template #header>
							<div v-tooltip="'Imports de base de données'">
								<i class="fas fa-database icone-rubrique"></i>
							</div>
						</template>
						<label class="intitule a-venir">(À venir)</label>
					</TabPanel>
					<TabPanel>
						<template #header>
							<div v-tooltip="'Imports de modèle'">
								<i class="fas fa-network-wired icone-rubrique"></i>
							</div>
						</template>
						<label class="intitule a-venir">(À venir)</label>
					</TabPanel>
				</TabView>
			</TabPanel>
			<TabPanel>
				<template #header>
					<div :class="{erreur: presenceRoutineEnErreur}"
					  v-tooltip="'Programmabilité'"><i class="fas fa-code icone-rubrique"></i></div>
				</template>
				<TabView v-model:activeIndex="ongletsSelectionnes.numSousOngletProgrammabilite">
					<TabPanel>
						<template #header>
							<div :class="{erreur: presenceRoutineEnErreur}" 
							v-tooltip="'Routines'">
								<i class="fas fa-gear icone-rubrique"></i>
							</div>
						</template>
						<OngletRoutines :routines="schema.routines" />
					</TabPanel>
					<TabPanel>
						<template #header>
							<div v-tooltip="'Fonctions'">
								<i class="fas fa-square-root-variable icone-rubrique"></i>
							</div>
						</template>
						<label class="intitule a-venir">(À venir)</label>
					</TabPanel>
					<TabPanel>
						<template #header>
							<div v-tooltip="'APIs'">
								<i class="fas fa-right-left icone-rubrique"></i>
							</div>
						</template>
						<label class="intitule a-venir">(À venir)</label>
					</TabPanel>
				</TabView>
			</TabPanel>
			<TabPanel>
				<template #header>
					<div v-tooltip="'Autres'"><i class="fas fa-ellipsis icone-rubrique"></i></div>
				</template>
				<TabView v-model:activeIndex="ongletsSelectionnes.numSousOngletAutres">
					<TabPanel>
						<template #header>
							<div v-tooltip="'Constantes'"><i class="fas fa-c icone-rubrique"></i></div>
						</template>
						<OngletConstantes :constantes="schema.constantes" />
					</TabPanel>
					<TabPanel>
						<template #header>
							<div v-tooltip="'Listes de valeurs'"><i class="fas fa-list-ul icone-rubrique"></i></div>
						</template>
						<OngletListeValeurs :listesValeurs="schema.listesValeurs" />
					</TabPanel>
				</TabView>
			</TabPanel>
		</TabView>
	</PanelRedimensionnable>
</template>

<style scoped>

.panel-gauche {
	padding: 2px 6px;
	flex-grow: 0;
}

.icone-rubrique {
	font-size: 16px;
}

.erreur {
	color: var(--couleur-valeur-en-erreur);
}

.a-venir {
	margin: 10px;
}

</style>